import {useEffect, useRef, useState} from 'react'
import {Badge, Button, Col, Form, Modal, Row, Spinner} from 'react-bootstrap'
import {FormattedMessage, MessageDescriptor, defineMessages, useIntl} from 'react-intl'
import {useLazyQuery, useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import {parseId} from '../../../helpers'
import {prependBackendUrl} from '../../../gql/client'
import {
  CREATE_ACA_VERIFICATION,
  UPDATE_ACA_VERIFICATION,
  IResCreateACAVerification,
  IResUpdateACAVerification,
} from '../../../gql/mutations/acaVerificationsMutations'
import {ACAVerificationRecord} from '../../../gql/queries/acaVerificationsQuery'
import {GET_ONE_POLICY_ATTACHMENTS_API} from '../../../gql/queries/policiesApiQueries'
import {EdgeMemberPolicy, IEdgesAttachments} from '../../../types/policiesApi'
import {ModalAddDocsPolicy} from './documents/ModalAddDocsPolicy'

type ResultColor = 'green' | 'yellow' | 'red' | 'blue'

export interface IModalACAVerificationProps {
  isOpen: boolean
  onClose: () => void
  policyId: string
  documentsDueDate?: string | null
  hasOpenTickets: boolean
  /** Pass an existing record to open in edit mode */
  verificationToEdit?: ACAVerificationRecord
  /** Called after a successful save so the tab can refresh */
  onSaved?: () => void
  canChange?: boolean
  members?: EdgeMemberPolicy[]
  /** Documents filtered by the verification's date — shown in edit/view mode */
  verificationDocs?: IEdgesAttachments[]
}

interface ACAForm {
  paymentStatus: 'up_to_date' | 'pending' | 'zero_payment' | ''
  paymentMethod: 'updated_in_system' | 'no_changes' | ''
  billingActionTaken: string
  pendingDocuments: 'yes' | 'no' | 'na' | ''
  docsActionTaken: string
  incomeModification: 'done' | 'no_changes_needed' | ''
  contactInfo: 'verified' | 'updated' | ''
  openTicketsDetail: string
  additionalNotes: string
}

const INIT_FORM: ACAForm = {
  paymentStatus: '',
  paymentMethod: '',
  billingActionTaken: '',
  pendingDocuments: '',
  docsActionTaken: '',
  incomeModification: '',
  contactInfo: '',
  openTicketsDetail: '',
  additionalNotes: '',
}

/**
 * Auto-compute the incentive segment based on billing + document answers.
 *
 * Green  – Payment up to date + no pending documents
 * Yellow – Payment up to date + pending documents
 * Red    – Payment pending/not confirmed  OR  $0 payment + pending docs (does not qualify)
 * Blue   – $0 premium + no/NA pending documents (doc-only follow-up segment)
 */
const computeResult = (paymentStatus: string, pendingDocuments: string): ResultColor | '' => {
  if (!paymentStatus || !pendingDocuments) return ''
  if (paymentStatus === 'zero_payment' && pendingDocuments === 'yes') return 'red'
  if (paymentStatus === 'zero_payment') return 'blue'
  if (paymentStatus === 'up_to_date' && (pendingDocuments === 'no' || pendingDocuments === 'na'))
    return 'green'
  if (paymentStatus === 'up_to_date' && pendingDocuments === 'yes') return 'yellow'
  if (paymentStatus === 'pending') return 'red'
  return ''
}

const emDash = '—'

/** Los 4 resultados reusan las llaves ya existentes del filtro ACA de la lista (§17.10). */
const RESULT_MSG: Record<ResultColor, MessageDescriptor> = defineMessages({
  green: {id: 'policies.list.filters.aca.green', defaultMessage: 'Paid & Completed'},
  yellow: {id: 'policies.list.filters.aca.yellow', defaultMessage: 'Paid / Pending Documents'},
  red: {id: 'policies.list.filters.aca.red', defaultMessage: 'Unpaid / Pending'},
  blue: {id: 'policies.list.filters.aca.blue', defaultMessage: '$0 Premium / Completed'},
}) as Record<ResultColor, MessageDescriptor>

const RESULT_STYLE: Record<ResultColor, {bg: string; textClass: string}> = {
  green: {bg: '#198754', textClass: 'text-white'},
  yellow: {bg: '#ffc107', textClass: 'text-dark'},
  red: {bg: '#dc3545', textClass: 'text-white'},
  blue: {bg: '#0d6efd', textClass: 'text-white'},
}

/**
 * Llaves NUEVAS que solo se consumen vía el helper `t()`: se declaran aquí para que el extractor
 * las capture (guía §17.12).
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const M = defineMessages({
  editTitle: {id: 'policies.detail.aca.modal.editTitle', defaultMessage: 'Edit ACA Verification'},
  newTitle: {id: 'policies.detail.aca.modal.newTitle', defaultMessage: 'ACA Verification Checklist'},
  methodUpdated: {id: 'policies.detail.aca.method.updated', defaultMessage: 'Updated in System'},
  methodNoChanges: {id: 'policies.detail.aca.method.noChanges', defaultMessage: 'No changes'},
  billingActionPlaceholder: {
    id: 'policies.detail.aca.billingActionPlaceholder',
    defaultMessage: 'e.g., New card charged / Payment due date reminder sent',
  },
  docsActionPlaceholder: {
    id: 'policies.detail.aca.docsActionPlaceholder',
    defaultMessage: 'e.g., I-94 photo uploaded / Income letter requested',
  },
  incomeDone: {id: 'policies.detail.aca.income.done', defaultMessage: 'Done'},
  incomeNoChangesNeeded: {
    id: 'policies.detail.aca.income.noChangesNeeded',
    defaultMessage: 'No changes needed',
  },
  contactVerified: {id: 'policies.detail.aca.contact.verified', defaultMessage: 'Verified'},
  contactUpdated: {id: 'policies.detail.aca.contact.updated', defaultMessage: 'Updated'},
  hasTickets: {id: 'policies.detail.aca.hasTickets', defaultMessage: 'This policy has active tickets'},
  noTickets: {id: 'policies.detail.aca.noTickets', defaultMessage: 'No open tickets on this policy'},
  ticketsPlaceholder: {
    id: 'policies.detail.aca.ticketsPlaceholder',
    defaultMessage: 'Describe the reason for open ticket(s)...',
  },
  notesPlaceholder: {
    id: 'policies.detail.aca.notesPlaceholder',
    defaultMessage: 'Write any relevant details from the conversation with the client...',
  },
  updateVerification: {
    id: 'policies.detail.aca.updateVerification',
    defaultMessage: 'Update Verification',
  },
  saveVerification: {id: 'policies.detail.aca.saveVerification', defaultMessage: 'Save Verification'},
  toastUpdated: {
    id: 'policies.detail.aca.toast.updated',
    defaultMessage: 'ACA Verification updated successfully',
  },
  toastUpdateFailed: {id: 'policies.detail.aca.toast.updateFailed', defaultMessage: 'Update failed'},
  toastSaved: {
    id: 'policies.detail.aca.toast.saved',
    defaultMessage: 'ACA Verification saved successfully',
  },
  toastSaveFailed: {id: 'policies.detail.aca.toast.saveFailed', defaultMessage: 'Save failed'},
})

const formatDeadline = (dateStr: string | null | undefined): string => {
  if (!dateStr) return emDash
  try {
    return new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    })
  } catch {
    return dateStr
  }
}

const recordToForm = (v: ACAVerificationRecord): ACAForm => ({
  paymentStatus: (v.paymentStatus as ACAForm['paymentStatus']) || '',
  paymentMethod: (v.paymentMethod as ACAForm['paymentMethod']) || '',
  billingActionTaken: v.billingActionTaken || '',
  pendingDocuments: (v.pendingDocuments as ACAForm['pendingDocuments']) || '',
  docsActionTaken: v.docsActionTaken || '',
  incomeModification: (v.incomeModification as ACAForm['incomeModification']) || '',
  contactInfo: (v.contactInfo as ACAForm['contactInfo']) || '',
  openTicketsDetail: v.openTicketsDetail || '',
  additionalNotes: v.additionalNotes || '',
})

export const ModalACAVerification: React.FC<IModalACAVerificationProps> = ({
  isOpen,
  onClose,
  policyId,
  documentsDueDate,
  hasOpenTickets,
  verificationToEdit,
  onSaved,
  canChange,
  members = [],
  verificationDocs,
}) => {
  const intl = useIntl()
  const t = (id: string, defaultMessage: string) => intl.formatMessage({id, defaultMessage})
  const isEditMode = !!verificationToEdit
  const readOnly = isEditMode && !canChange

  const [isUploadDocsOpen, setIsUploadDocsOpen] = useState(false)
  const [sessionDocs, setSessionDocs] = useState<IEdgesAttachments[]>([])
  const knownDocIds = useRef<Set<string>>(new Set())

  const [fetchDocs] = useLazyQuery<{
    policiesById: {policyAttachmentsSet: {edges: IEdgesAttachments[]}}
  }>(GET_ONE_POLICY_ATTACHMENTS_API, {fetchPolicy: 'no-cache'})

  // Snapshot existing doc IDs when the modal opens so we can diff after upload
  useEffect(() => {
    if (!isOpen || !policyId) return
    setSessionDocs([])
    knownDocIds.current = new Set()
    fetchDocs({variables: {id: parseId(policyId)}}).then((res) => {
      const ids =
        res.data?.policiesById?.policyAttachmentsSet?.edges.map((e) => e.node.id) || []
      knownDocIds.current = new Set(ids)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, policyId])

  const handleAfterUpload = async () => {
    const res = await fetchDocs({variables: {id: parseId(policyId)}})
    const allEdges = res.data?.policiesById?.policyAttachmentsSet?.edges || []
    const newEdges = allEdges.filter((e) => !knownDocIds.current.has(e.node.id))
    newEdges.forEach((e) => knownDocIds.current.add(e.node.id))
    if (newEdges.length > 0) setSessionDocs((prev) => [...prev, ...newEdges])
  }

  const [form, setForm] = useState<ACAForm>(
    verificationToEdit ? recordToForm(verificationToEdit) : {...INIT_FORM}
  )

  // Re-populate form whenever the edit target changes (e.g., user opens a different row)
  useEffect(() => {
    setForm(verificationToEdit ? recordToForm(verificationToEdit) : {...INIT_FORM})
  }, [verificationToEdit])

  const set = (field: keyof ACAForm, value: string) => {
    if (readOnly) return
    setForm((prev) => ({...prev, [field]: value}))
  }

  const [gqlCreate, {loading: loadingCreate}] =
    useMutation<IResCreateACAVerification>(CREATE_ACA_VERIFICATION)
  const [gqlUpdate, {loading: loadingUpdate}] =
    useMutation<IResUpdateACAVerification>(UPDATE_ACA_VERIFICATION)

  const loading = loadingCreate || loadingUpdate

  const result = computeResult(form.paymentStatus, form.pendingDocuments)
  const resultStyle = result ? RESULT_STYLE[result] : null
  const resultLabel = result ? intl.formatMessage(RESULT_MSG[result]) : ''

  const buildInput = () => ({
    policyId: Number(parseId(policyId)),
    paymentStatus: form.paymentStatus,
    paymentMethod: form.paymentMethod,
    billingActionTaken: form.billingActionTaken,
    pendingDocuments: form.pendingDocuments,
    docsActionTaken: form.docsActionTaken,
    incomeModification: form.incomeModification,
    contactInfo: form.contactInfo,
    result: result || '',
    openTicketsDetail: form.openTicketsDetail,
    additionalNotes: form.additionalNotes,
  })

  const handleSave = async () => {
    if (readOnly) return

    if (isEditMode && verificationToEdit) {
      const {data} = await gqlUpdate({
        variables: {id: Number(parseId(verificationToEdit.id)), data: buildInput()},
      })
      if (data?.updatePolicyAcaVerification?.success) {
        toast.success(t('policies.detail.aca.toast.updated', 'ACA Verification updated successfully'))
        onSaved?.()
        handleClose()
      } else {
        const msg =
          data?.updatePolicyAcaVerification?.errors?.[0]?.message ||
          t('policies.detail.aca.toast.updateFailed', 'Update failed')
        toast.error(msg)
      }
    } else {
      const {data} = await gqlCreate({variables: {data: buildInput()}})
      if (data?.createPolicyAcaVerification?.success) {
        toast.success(t('policies.detail.aca.toast.saved', 'ACA Verification saved successfully'))
        onSaved?.()
        handleClose()
      } else {
        const msg =
          data?.createPolicyAcaVerification?.errors?.[0]?.message ||
          t('policies.detail.aca.toast.saveFailed', 'Save failed')
        toast.error(msg)
      }
    }
  }

  const handleClose = () => {
    setForm({...INIT_FORM})
    onClose()
  }

  return (
    <Modal show={isOpen} onHide={handleClose} size='lg' scrollable>
      <Modal.Header closeButton className='bg-light'>
        <Modal.Title>
          <i className='bi bi-clipboard2-check me-2 text-primary' /> {isEditMode
            ? t('policies.detail.aca.modal.editTitle', 'Edit ACA Verification')
            : t('policies.detail.aca.modal.newTitle', 'ACA Verification Checklist')}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* ── 1. BILLING STATUS ── */}
        <section className='mb-4'>
          <h6 className='fw-bold text-uppercase text-primary border-bottom pb-1 mb-3'>
            <FormattedMessage id='policies.detail.aca.section.billing' defaultMessage='1. Billing Status' />
          </h6>

          <Form.Group className='mb-3'>
            <Form.Label className='fw-semibold mb-1'>
              <FormattedMessage id='policies.list.filters.field.paymentStatus' defaultMessage='Payment status' />
            </Form.Label>
            <div className='d-flex flex-wrap gap-3'>
              {(
                [
                  {value: 'up_to_date', label: t('policies.detail.aca.payment.upToDate', 'Up to date')},
                  {value: 'pending', label: t('policies.detail.aca.payment.pending', 'Pending')},
                  {value: 'zero_payment', label: t('policies.detail.aca.payment.zero', '$0 Payment')},
                ] as const
              ).map(({value, label}) => (
                <Form.Check
                  key={value}
                  type='radio'
                  id={`paymentStatus_${value}`}
                  name='paymentStatus'
                  label={label}
                  disabled={readOnly}
                  checked={form.paymentStatus === value}
                  onChange={() => set('paymentStatus', value)}
                />
              ))}
            </div>
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label className='fw-semibold mb-1'>
              <FormattedMessage id='policies.detail.aca.paymentMethod' defaultMessage='Payment Method' />
            </Form.Label>
            <div className='d-flex flex-wrap gap-3'>
              {(
                [
                  {value: 'updated_in_system', label: t('policies.detail.aca.method.updated', 'Updated in System')},
                  {value: 'no_changes', label: t('policies.detail.aca.method.noChanges', 'No changes')},
                ] as const
              ).map(({value, label}) => (
                <Form.Check
                  key={value}
                  type='radio'
                  id={`paymentMethod_${value}`}
                  name='paymentMethod'
                  label={label}
                  disabled={readOnly}
                  checked={form.paymentMethod === value}
                  onChange={() => set('paymentMethod', value)}
                />
              ))}
            </div>
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label className='fw-semibold mb-1'>
              <FormattedMessage id='policies.detail.aca.actionTaken' defaultMessage='Action Taken' />
            </Form.Label>
            <Form.Control
              type='text'
              placeholder={t(
                'policies.detail.aca.billingActionPlaceholder',
                'e.g., New card charged / Payment due date reminder sent'
              )}
              disabled={readOnly}
              value={form.billingActionTaken}
              onChange={(e) => set('billingActionTaken', e.target.value)}
            />
          </Form.Group>
        </section>

        {/* ── 2. DOCUMENT MANAGEMENT ── */}
        <section className='mb-4'>
          <h6 className='fw-bold text-uppercase text-primary border-bottom pb-1 mb-3'>
            <FormattedMessage
              id='policies.detail.aca.section.documents'
              defaultMessage='2. Document Management (Marketplace)'
            />
          </h6>

          <Form.Group className='mb-3'>
            <Form.Label className='fw-semibold mb-1'>
              <FormattedMessage id='policies.detail.aca.pendingDocuments' defaultMessage='Pending Documents' />
            </Form.Label>
            <div className='d-flex flex-wrap gap-3'>
              {(
                [
                  {value: 'yes', label: t('common.boolean.yes', 'Yes')},
                  {value: 'no', label: t('common.boolean.no', 'No')},
                  {value: 'na', label: t('common.value.notAvailable', 'N/A')},
                ] as const
              ).map(({value, label}) => (
                <Form.Check
                  key={value}
                  type='radio'
                  id={`pendingDocuments_${value}`}
                  name='pendingDocuments'
                  label={label}
                  disabled={readOnly}
                  checked={form.pendingDocuments === value}
                  onChange={() => set('pendingDocuments', value)}
                />
              ))}
            </div>
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label className='fw-semibold mb-1'>
              <FormattedMessage id='policies.detail.aca.actionTaken' defaultMessage='Action Taken' />
            </Form.Label>
            <Form.Control
              type='text'
              placeholder={t(
                'policies.detail.aca.docsActionPlaceholder',
                'e.g., I-94 photo uploaded / Income letter requested'
              )}
              disabled={readOnly}
              value={form.docsActionTaken}
              onChange={(e) => set('docsActionTaken', e.target.value)}
            />
          </Form.Group>

          <div className='mb-0'>
            <Form.Label className='fw-semibold mb-1'>
              <FormattedMessage
                id='policies.detail.aca.marketplaceDeadline'
                defaultMessage='Marketplace Deadline'
              />
            </Form.Label>
            <div className='d-flex align-items-center gap-2'>
              <i className='bi bi-calendar-event text-muted' />
              <span className={`fw-medium ${!documentsDueDate ? 'text-muted fst-italic' : ''}`}>
                {formatDeadline(documentsDueDate)}
              </span>
              {documentsDueDate && (
                <Badge bg='secondary' className='ms-1'>
                  <FormattedMessage id='policies.detail.aca.fromPolicy' defaultMessage='from policy' />
                </Badge>
              )}
            </div>
          </div>
        </section>

        {/* ── 3. PROFILE UPDATE ── */}
        <section className='mb-4'>
          <h6 className='fw-bold text-uppercase text-primary border-bottom pb-1 mb-3'>
            <FormattedMessage id='policies.detail.aca.section.profile' defaultMessage='3. Profile Update' />
          </h6>

          <Row>
            <Col sm={6}>
              <Form.Group className='mb-3'>
                <Form.Label className='fw-semibold mb-1'>
                  <FormattedMessage
                    id='policies.detail.aca.incomeModification'
                    defaultMessage='Income Modification'
                  />
                </Form.Label>
                <div className='d-flex flex-wrap gap-3'>
                  {(
                    [
                      {value: 'done', label: t('policies.detail.aca.income.done', 'Done')},
                      {
                        value: 'no_changes_needed',
                        label: t('policies.detail.aca.income.noChangesNeeded', 'No changes needed'),
                      },
                    ] as const
                  ).map(({value, label}) => (
                    <Form.Check
                      key={value}
                      type='radio'
                      id={`incomeModification_${value}`}
                      name='incomeModification'
                      label={label}
                      disabled={readOnly}
                      checked={form.incomeModification === value}
                      onChange={() => set('incomeModification', value)}
                    />
                  ))}
                </div>
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group className='mb-3'>
                <Form.Label className='fw-semibold mb-1'>
                  <FormattedMessage
                    id='policies.detail.aca.contactInfo'
                    defaultMessage='Contact Information'
                  />
                </Form.Label>
                <div className='d-flex flex-wrap gap-3'>
                  {(
                    [
                      {value: 'verified', label: t('policies.detail.aca.contact.verified', 'Verified')},
                      {value: 'updated', label: t('policies.detail.aca.contact.updated', 'Updated')},
                    ] as const
                  ).map(({value, label}) => (
                    <Form.Check
                      key={value}
                      type='radio'
                      id={`contactInfo_${value}`}
                      name='contactInfo'
                      label={label}
                      disabled={readOnly}
                      checked={form.contactInfo === value}
                      onChange={() => set('contactInfo', value)}
                    />
                  ))}
                </div>
              </Form.Group>
            </Col>
          </Row>
        </section>

        {/* ── 4. FINAL MANAGEMENT STATUS ── */}
        <section className='mb-4'>
          <h6 className='fw-bold text-uppercase text-primary border-bottom pb-1 mb-3'>
            <FormattedMessage
              id='policies.detail.aca.section.final'
              defaultMessage='4. Final Management Status'
            />
          </h6>

          <Row>
            <Col sm={6} className='mb-3'>
              <Form.Label className='fw-semibold mb-2'>
                <FormattedMessage id='policies.detail.aca.th.result' defaultMessage='Result' />
              </Form.Label>
              {resultStyle ? (
                <div
                  className={`rounded px-3 py-2 d-inline-flex align-items-center fw-semibold ${resultStyle.textClass}`}
                  style={{backgroundColor: resultStyle.bg}}
                >
                  {resultLabel}
                </div>
              ) : (
                <p className='text-muted fst-italic small mb-0'>
                  <FormattedMessage
                    id='policies.detail.aca.resultHint'
                    defaultMessage='Auto-calculated once Payment Status and Pending Documents are selected.'
                  />
                </p>
              )}
            </Col>

            <Col sm={6} className='mb-3'>
              <Form.Label className='fw-semibold mb-2'>
                <FormattedMessage id='policies.detail.aca.openTickets' defaultMessage='Open Tickets' />
              </Form.Label>
              <div className='d-flex align-items-center gap-2 mb-2'>
                <Badge bg={hasOpenTickets ? 'danger' : 'success'} className='px-2 py-1'>
                  {hasOpenTickets ? t('common.boolean.yes', 'Yes') : t('common.boolean.no', 'No')}
                </Badge>
                <span className='text-muted small'>
                  {hasOpenTickets
                    ? t('policies.detail.aca.hasTickets', 'This policy has active tickets')
                    : t('policies.detail.aca.noTickets', 'No open tickets on this policy')}
                </span>
              </div>
              {hasOpenTickets && (
                <Form.Control
                  type='text'
                  placeholder={t(
                    'policies.detail.aca.ticketsPlaceholder',
                    'Describe the reason for open ticket(s)...'
                  )}
                  disabled={readOnly}
                  value={form.openTicketsDetail}
                  onChange={(e) => set('openTicketsDetail', e.target.value)}
                />
              )}
            </Col>
          </Row>
        </section>

        {/* ── ADDITIONAL NOTES ── */}
        <section className='mb-2'>
          <h6 className='fw-bold text-uppercase text-primary border-bottom pb-1 mb-3'>
            <FormattedMessage
              id='policies.detail.aca.section.notes'
              defaultMessage='Additional Notes'
            />
          </h6>
          <Form.Control
            as='textarea'
            rows={3}
            placeholder={t(
              'policies.detail.aca.notesPlaceholder',
              'Write any relevant details from the conversation with the client...'
            )}
            disabled={readOnly}
            value={form.additionalNotes}
            onChange={(e) => set('additionalNotes', e.target.value)}
          />
        </section>

        {/* ── SUPPORTING DOCUMENTS ── */}
        <section className='mb-2 mt-4'>
          <h6 className='fw-bold text-uppercase text-primary border-bottom pb-1 mb-3'>
            <FormattedMessage
              id='policies.detail.aca.section.supportingDocs'
              defaultMessage='Supporting Documents'
            />
          </h6>

          {isEditMode ? (
            /* View mode: show documents uploaded on the verification's date */
            <>
              {verificationDocs && verificationDocs.length > 0 ? (
                <div className='d-flex flex-column gap-2'>
                  {verificationDocs.map(({node}) => (
                    <div
                      key={node.id}
                      className='d-flex align-items-center gap-2 border rounded px-3 py-2'
                    >
                      <i className='bi bi-file-earmark text-muted fs-5' />
                      <div className='flex-grow-1 overflow-hidden'>
                        <div className='fw-medium text-truncate'>{node.name}</div>
                        {node.memberPolicy && (
                          <div className='text-muted small'>
                            {node.memberPolicy.firstName} {node.memberPolicy.lastName}
                          </div>
                        )}
                      </div>
                      {node.attachments && (
                        <a
                          href={prependBackendUrl(node.attachments)}
                          target='_blank'
                          rel='noreferrer'
                          className='btn btn-sm btn-outline-secondary'
                        >
                          <i className='bi bi-download' />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className='text-muted small mb-0'>
                  <FormattedMessage
                    id='policies.detail.aca.noDocsForDate'
                    defaultMessage='No documents found for this verification date.'
                  />
                </p>
              )}
            </>
          ) : (
            /* Create mode: upload button + session docs */
            <>
              <Button
                variant='outline-primary'
                size='sm'
                className='mb-3'
                onClick={() => setIsUploadDocsOpen(true)}
              >
                <i className='bi bi-upload me-1' /> <FormattedMessage
                  id='policies.detail.tab.docs.uploadFiles'
                  defaultMessage='Upload Files'
                />
              </Button>
              {sessionDocs.length > 0 && (
                <div className='d-flex flex-column gap-2'>
                  {sessionDocs.map(({node}) => (
                    <div
                      key={node.id}
                      className='d-flex align-items-center gap-2 border rounded px-3 py-2'
                    >
                      <i className='bi bi-file-earmark text-muted fs-5' />
                      <div className='flex-grow-1 overflow-hidden'>
                        <div className='fw-medium text-truncate'>{node.name}</div>
                        {node.memberPolicy && (
                          <div className='text-muted small'>
                            {node.memberPolicy.firstName} {node.memberPolicy.lastName}
                          </div>
                        )}
                      </div>
                      {node.attachments && (
                        <a
                          href={prependBackendUrl(node.attachments)}
                          target='_blank'
                          rel='noreferrer'
                          className='btn btn-sm btn-outline-secondary'
                        >
                          <i className='bi bi-download' />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {sessionDocs.length === 0 && (
                <p className='text-muted small mb-0'>
                  <FormattedMessage
                    id='policies.detail.aca.noDocsInSession'
                    defaultMessage='No documents uploaded in this session.'
                  />
                </p>
              )}
            </>
          )}
        </section>
      </Modal.Body>

      <Modal.Footer>
        {resultStyle && (
          <span
            className={`badge fs-6 px-3 py-2 me-auto ${resultStyle.textClass}`}
            style={{backgroundColor: resultStyle.bg}}
          >
            {resultLabel}
          </span>
        )}
        <Button variant='secondary' onClick={handleClose}>
          <FormattedMessage id='common.action.close' defaultMessage='Close' />
        </Button>
        {!readOnly && (
          <Button variant='primary' onClick={handleSave} disabled={loading}>
            {loading && <Spinner animation='border' size='sm' className='me-2' />}
            <i className='bi bi-clipboard2-check me-1' /> {isEditMode
              ? t('policies.detail.aca.updateVerification', 'Update Verification')
              : t('policies.detail.aca.saveVerification', 'Save Verification')}
          </Button>
        )}
      </Modal.Footer>

      {isUploadDocsOpen && (
        <ModalAddDocsPolicy
          id={policyId}
          isOpen={isUploadDocsOpen}
          onClose={() => setIsUploadDocsOpen(false)}
          refetch={handleAfterUpload}
          members={members}
        />
      )}
    </Modal>
  )
}
